// Chapter 0's disk-cost illustration.
//
// IMPORTANT: unlike every other demo on this site, this one is NOT the
// engine. The engine has no B-tree to compare against, and a browser cannot
// measure real disk seeks. This is an illustration of the cost model, driven
// by published latency figures, and it is labelled as such in the UI. Being
// explicit matters: the rest of the site earns trust by being real, and that
// trust would be worth nothing if this panel quietly pretended too.

const SEEK_MS = 8; // 7200rpm seek + rotational latency, order of magnitude
const SEQ_MS = 0.05; // appending to an already-open sequential stream

export class IoDemo {
  private host: HTMLElement;
  private writes = 0;
  private randomMs = 0;
  private seqMs = 0;
  private els: Record<string, HTMLElement> = {};

  constructor(host: HTMLElement) {
    this.host = host;
    this.build();
    this.render();
  }

  private build(): void {
    this.host.classList.add("io-demo");

    const controls = document.createElement("div");
    controls.className = "demo-controls";
    const add = (label: string, n: number) => {
      const b = document.createElement("button");
      b.textContent = label;
      b.onclick = () => {
        this.writes += n;
        this.randomMs += n * SEEK_MS;
        this.seqMs += n * SEQ_MS;
        this.render();
      };
      controls.appendChild(b);
    };
    add("write 10 records", 10);
    add("write 100 records", 100);
    const reset = document.createElement("button");
    reset.textContent = "reset";
    reset.className = "demo-reset";
    reset.onclick = () => {
      this.writes = this.randomMs = this.seqMs = 0;
      this.render();
    };
    controls.appendChild(reset);
    this.host.appendChild(controls);

    const stage = document.createElement("div");
    stage.className = "io-stage";
    for (const [key, title, sub] of [
      ["random", "update in place (B-tree)", "one seek per record"],
      ["seq", "append sequentially (LSM)", "no seek, just append"],
    ] as const) {
      const col = document.createElement("div");
      col.className = "io-col";
      const h = document.createElement("div");
      h.className = "io-title";
      h.textContent = title;
      const s = document.createElement("div");
      s.className = "io-sub";
      s.textContent = sub;
      const bar = document.createElement("div");
      bar.className = "io-bar";
      const fill = document.createElement("div");
      fill.className = `io-fill io-fill-${key}`;
      bar.appendChild(fill);
      const val = document.createElement("div");
      val.className = "io-val";
      this.els[key + "Fill"] = fill;
      this.els[key + "Val"] = val;
      col.append(h, s, bar, val);
      stage.appendChild(col);
    }
    this.host.appendChild(stage);

    this.els.verdict = document.createElement("div");
    this.els.verdict.className = "io-verdict";
    this.host.appendChild(this.els.verdict);

    const note = document.createElement("div");
    note.className = "demo-note demo-note-warn";
    note.textContent =
      "Illustration, not a measurement — this is the only panel on the site that isn't the live engine. " +
      "It models a spinning disk at ~8 ms per seek versus ~0.05 ms per sequential append. On an SSD the gap is far " +
      "smaller in time but the same in kind, and write amplification starts to matter more than seek time.";
    this.host.appendChild(note);
  }

  private render(): void {
    const max = Math.max(this.randomMs, 1);
    this.els.randomFill.style.width = "100%";
    this.els.seqFill.style.width = `${Math.max(0.4, (this.seqMs / max) * 100)}%`;
    this.els.randomVal.textContent = fmt(this.randomMs);
    this.els.seqVal.textContent = fmt(this.seqMs);
    this.els.verdict.textContent = this.writes
      ? `${this.writes} records — appending is about ${(this.randomMs / Math.max(this.seqMs, 0.0001)).toFixed(0)}× faster`
      : "Write some records to compare the two strategies.";
  }
}

function fmt(ms: number): string {
  if (ms === 0) return "0 ms";
  return ms >= 1000 ? (ms / 1000).toFixed(2) + " s" : ms.toFixed(1) + " ms";
}
